import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../../components/Footer/Footer';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { images } from '../../components/shared/assets';
import { usePageContent, useBlogPosts } from '../../hooks/useContentful';
import { ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import { ContentfulImage } from '../../components/Contentful';
import { FilterBar, DESTINATION_OPTIONS, TYPE_OPTIONS } from '../../components/shared/FilterBar';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { PageToggleButton } from '../../components/shared/PageToggleButton/PageToggleButton';
import { useBlogXML } from '../../hooks/useBlogXML';
import { useBlogFiltering } from '../../hooks/useBlogFiltering';
import { BlogPost } from '../../utils/browserXMLParser';
import { createSelfbookHandler } from '../../utils/selfbook';
import './BlogPage.css';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleBooking = createSelfbookHandler(navigate);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDestination, setSelectedDestination] = React.useState<string | undefined>(
    searchParams.get('destination') || undefined
  );
  const [selectedType, setSelectedType] = React.useState<string | undefined>(
    searchParams.get('type') || undefined
  );
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const postsPerPage = 12;
  
  // Update URL when filters change
  React.useEffect(() => {
    const newParams = new URLSearchParams();
    if (selectedDestination) newParams.set('destination', selectedDestination);
    if (selectedType) newParams.set('type', selectedType);
    if (currentPage > 1) newParams.set('page', currentPage.toString());
    
    setSearchParams(newParams, { replace: true });
  }, [selectedDestination, selectedType, currentPage, setSearchParams]);
  
  // Fetch content from XML and Contentful
  const { posts: allXMLPosts, loading: xmlLoading } = useBlogXML();
  const { data: pageContentData } = usePageContent('blog');
  const { data: blogPostsData } = useBlogPosts(20);
  const pageContent = pageContentData?.[0]?.fields as any;
  
  // Apply filtering to all posts
  const { filteredPosts } = useBlogFiltering(allXMLPosts, {
    destination: selectedDestination,
    type: selectedType
  });
  
  // Priority: Filtered XML posts > Contentful posts > Static fallback
  const allBlogPosts: BlogPost[] = filteredPosts.length > 0 ? filteredPosts : [];
  const contentfulFallback = blogPostsData || [];
  
  // Calculate pagination for XML posts
  const totalPages = Math.ceil(allBlogPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const paginatedPosts: BlogPost[] = allBlogPosts.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  React.useEffect(() => {
    if (currentPage > 1 && (selectedDestination || selectedType)) {
      setSearchParams({ page: '1' });
    }
  }, [selectedDestination, selectedType, setSearchParams, currentPage]);
  
  // Initialize all interactive features
  useEffect(() => {
    const cleanup = initializeAllFeatures();
    return cleanup;
  }, []);

  return (
    <div className="blog-page">
      {/* Navigation */}
      <SmartNavigation 
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
      />

      {/* Hero Section - Diptych Layout */}
      <div className="blog-hero-section">
        <div className="blog-hero-left">
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">
              {t('pages/blog:hero.title')}
            </h1>
          </div>
          <div 
            className="blog-hero-watermark-bottom" 
            style={{ backgroundImage: `url(${images.destinationsPage?.figmaAssets?.destinationCarousel?.atacamaGraphic || '/assets/figma/f78d9ea45f8dc3a1b73ef28f0830d6c98fc6c32d.png'})` }}
          />
        </div>
        <div className="blog-hero-right" style={{ backgroundImage: `url(${images.blogPage.heroBackground})` }} />
      </div>

      {/* Introduction Section */}
      <div className="blog-intro-section">
        <div className="blog-intro-section-inner">
          <p className="blog-intro-text">
            {t('pages/blog:intro.text')}
          </p>
        </div>
      </div>

      {/* Filter Buttons Section */}
      <div className="blog-filters-section">
        <FilterBar
          className="blog-filters"
          dropdowns={[
            {
              id: 'destination',
              type: 'destination',
              level: 'brand',
              options: DESTINATION_OPTIONS,
              selectedValue: selectedDestination,
              onSelect: (value: string) => setSelectedDestination(value === selectedDestination ? undefined : value)
            },
            {
              id: 'type',
              type: 'type',
              level: 'brand',
              options: TYPE_OPTIONS,
              selectedValue: selectedType,
              onSelect: (value: string) => setSelectedType(value === selectedType ? undefined : value)
            }
          ]}
          alignment="left"
        />
        
        {/* Filter Results Counter and Clear Button */}
        <div className="blog-filter-results">
          {(selectedDestination || selectedType) && (
            <div className="blog-filter-info">
              <span className="blog-filter-count">
                {selectedDestination && selectedType
                  ? t('pages/blog:filters.resultsForIn', {
                      count: filteredPosts.length,
                      destination: DESTINATION_OPTIONS.find(opt => opt.value === selectedDestination)?.label.split(',')[0],
                      type: TYPE_OPTIONS.find(opt => opt.value === selectedType)?.label
                    })
                  : selectedDestination
                  ? t('pages/blog:filters.resultsFor', {
                      count: filteredPosts.length,
                      destination: DESTINATION_OPTIONS.find(opt => opt.value === selectedDestination)?.label.split(',')[0]
                    })
                  : selectedType
                  ? t('pages/blog:filters.resultsIn', {
                      count: filteredPosts.length,
                      type: TYPE_OPTIONS.find(opt => opt.value === selectedType)?.label
                    })
                  : t('pages/blog:filters.results', { count: filteredPosts.length })
                }
              </span>
              <HandDrawnButton
                text={t('pages/blog:filters.clearFilters')}
                onClick={() => {
                  setSelectedDestination(undefined);
                  setSelectedType(undefined);
                  setSearchParams({ page: '1' });
                }}
                variant="brand"
                color="awasi"
                animation="stroke-to-stroke"
                size="normal"
                className="blog-filter-clear-button"
              />
            </div>
          )}
        </div>
      </div>

      {/* Blog Posts Grid - 2 Column Layout */}
      <div className="blog-content-container" data-scroll-section="blog-posts">
        
        <div className="blog-posts-grid">
          {paginatedPosts.length > 0 ? (
            // Render filtered and paginated XML posts
            paginatedPosts.map((post: BlogPost) => {
              const formatDate = (date: Date) => {
                return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
              };
              
              return (
                <article key={post.id} className="blog-post-card">
                  <div className="blog-post-image">
                    <img 
                      src={post.featuredImage || images.galleryWaterfallLandscape} 
                      alt={post.title} 
                    />
                  </div>
                  <div className="blog-post-content">
                    <h3 className="blog-post-title">
                      {post.title}
                    </h3>
                    <div className="blog-post-meta">
                      <span className="blog-post-author">
                        {post.author}
                      </span>
                      <span className="blog-post-separator">-</span>
                      <span className="blog-post-date">
                        {formatDate(post.publishDate)}
                      </span>
                    </div>
                    <p className="blog-post-excerpt">
                      {post.excerpt || post.content.substring(0, 150) + '...'}
                    </p>
                    <div className="blog-post-links">
                      <Link to={`/blog/${post.slug}`} className="blog-post-link">
                        <span>{t('pages/blog:posts.readMore')}</span>
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })
          ) : contentfulFallback.length > 0 ? (
            // Render Contentful posts
            contentfulFallback.map((post) => {
              const categories = post.fields.category || [];
              const categoryString = Array.isArray(categories) ? categories.join(' • ') : '';
              const featuredImage = post.fields.featuredImage as any;
              const featuredImageUrl = featuredImage?.fields?.file?.url;
              const slug = post.fields.slug || post.sys.id;
              
              return (
                <article key={post.sys.id} className="blog-post-card">
                  <div className="blog-post-image">
                    <img 
                      src={featuredImageUrl ? `https:${featuredImageUrl}` : images.galleryWaterfallLandscape} 
                      alt={String(post.fields.title || 'Blog post')} 
                    />
                  </div>
                  <div className="blog-post-content">
                    <h3 className="blog-post-title">
                      <ContentfulText 
                        content={post.fields.title} 
                        fallback="Untitled Post"
                      />
                    </h3>
                    <div className="blog-post-meta">
                      <span className="blog-post-author">
                        <ContentfulText 
                          content={post.fields.author || 'AWASI PATAGONIA'} 
                          fallback="AWASI PATAGONIA"
                        />
                      </span>
                      <span className="blog-post-separator">-</span>
                      <span className="blog-post-date">
                        <ContentfulText 
                          content={post.fields.date || '18.02.2022'} 
                          fallback="18.02.2022"
                        />
                      </span>
                    </div>
                    <p className="blog-post-excerpt">
                      <ContentfulText 
                        content={post.fields.excerpt} 
                        fallback="Lorem ipsum dolor sit amet, consectetur adipiscing elit."
                      />
                    </p>
                    <div className="blog-post-links">
                      <Link to={`/blog/${slug}`} className="blog-post-link">
                        <span>{t('pages/blog:posts.readMore')}</span>
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })
          ) : (
            // Fallback static posts
            <>
              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryWaterfallLandscape} alt="Waterfall landscape" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Ingrid Weyland - Topographies of Fragility</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">03.02.2022</span>
                  </div>
                  <p className="blog-post-excerpt">
                    The Argentine-born artist and photographer employs a unique technique in her work: she applies crumpled images on top of her photographs to highlight the impact man has on nature.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/ingrid-weyland-topographies-of-fragility" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryCircularPool} alt="Pool reflection" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Teresa Marcaida - Nature, Art and Mankind</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">03.02.2022</span>
                  </div>
                  <p className="blog-post-excerpt">
                    As you watch, as you listen to the beauty of the evening, in that extraordinary quietness when almost everything seems to be motionless, you are then in complete communion, in complete harmony, with everything about you.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/teresa-marcaida-nature-art-and-mankind" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                    <Link to="/film-collection#teresa-marcaida" className="blog-post-link blog-post-link--secondary">
                      <span>{t('pages/blog:posts.watchVideo')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryGrid2} alt="Geyser landscape" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Rainer Gross - "Double Takes"</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">03.02.2022</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo. Elementum nibh justo viverra porttitor vulputate vestibulum.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/rainer-gross-double-takes" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                    <Link to="/film-collection#rainer-gross" className="blog-post-link blog-post-link--secondary">
                      <span>{t('pages/blog:posts.watchVideo')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-wildlife-1" fallbackSrc={images.galleryGrid3} alt="Mountain peaks" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Iliana Díaz López - "Portales del Ara Pyau"</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">03.02.2022</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo. Elementum nibh justo viverra porttitor vulputate vestibulum.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/iliana-diaz-lopez-portales-del-ara-pyau" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                    <Link to="/film-collection#iliana-diaz-lopez" className="blog-post-link blog-post-link--secondary">
                      <span>{t('pages/blog:posts.watchVideo')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryGrid1} alt="Charles Darwin" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Charles Darwin in Patagonia</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI PATAGONIA</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">02.09.2015</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Charles Darwin is perhaps the most famous visitor to have touched foot in Patagonia. A renowned British naturalist and geologist, Darwin formed part of the crew on The Beagle, a chartering ship that sailed around Chile's southern cone.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/charles-darwin-in-patagonia" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryGrid7} alt="Tehuelches Indians" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">The Tehuelches Indians: the native tribes of Patagonia</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI PATAGONIA</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">18.05.2015</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Sadly, disease and conflict brought about the demise of these people, but when the channels of Patagonia and Tierra del Fuego were first navigated, visitors happened upon a unique culture.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/tehuelches-indians-native-tribes-patagonia" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryDesertLandscape} alt="Astronomy Tour" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Awasi Atacama unveils new Astronomy Tour</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI ATACAMA</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">10.02.2017</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Experience the clearest skies on Earth with our new astronomy excursion. The Atacama Desert offers unparalleled stargazing opportunities, and our expert guides will take you on a journey through the cosmos.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/awasi-atacama-unveils-new-astronomy-tour" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryWaterfallLandscape} alt="Andrés Paredes" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Awasi Artist Immersion - Andrés Paredes</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI ATACAMA</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">28.08.2024</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Argentine visual artist Andrés Paredes returned for his second Awasi Artist Immersion programme, this time staying at the Awasi Atacama. Moving from the jungle into the otherworldly landscape of the Atacama Desert totally changed the nature of his work.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/awasi-artist-immersion-andres-paredes" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryCircularPool} alt="Carolina Salom" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Awasi Artist Immersion - Carolina Salom</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">05.11.2024</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Textile artist Carolina Salom delved into the world of natural dyes, experimenting with Misiones' iconic red Ñau mud, various jungle fungi, yerba mate, and roots from local trees.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/awasi-artist-immersion-carolina-salom" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryGrid2} alt="Angela Bulich" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Awasi Artist Immersion - Angela Bulich</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">10.08.2023</span>
                  </div>
                  <p className="blog-post-excerpt">
                    From biologist to jewellery designer, Angela Bulich creates sculptural jewellery experimentally. Everything I know about the craft I learned backwards, beginning making jewellery as art.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/awasi-artist-immersion-angela-bulich" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryGrid3} alt="Cecilia Nigro" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Awasi Artist Immersion - Cecilia Nigro</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">02.01.2024</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Argentinian ceramicist Cecilia Nigro explores the alchemy and intrinsic processes of material metamorphosis. Fragility, morphology, and the almost absence of colour permeate her works.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/awasi-artist-immersion-cecilia-nigro" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>

              <article className="blog-post-card">
                <div className="blog-post-image">
                  <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryGrid1} alt="Florencia Echeverría" />
                </div>
                <div className="blog-post-content">
                  <h3 className="blog-post-title">Awasi Artist Immersion - Florencia Echeverría</h3>
                  <div className="blog-post-meta">
                    <span className="blog-post-author">AWASI IGUAZÚ</span>
                    <span className="blog-post-separator">-</span>
                    <span className="blog-post-date">20.06.2024</span>
                  </div>
                  <p className="blog-post-excerpt">
                    Visual artist and sculptor Florencia Echeverría from Rosario, Argentina, explored the Atlantic Rainforest's biodiversity and created nature-inspired installations during her residency.
                  </p>
                  <div className="blog-post-links">
                    <Link to="/blog/awasi-artist-immersion-florencia-echeverria" className="blog-post-link">
                      <span>{t('pages/blog:posts.readMore')}</span>
                    </Link>
                  </div>
                </div>
              </article>
            </>
          )}

        </div>
      </div>

      {/* Pagination - Hand-drawn frames style */}
      {totalPages > 1 && (
        <div className="blog-pagination">
          <div className="blog-pagination-numbers">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = i + 1;
              const isActive = currentPage === pageNum;
              
              return (
                <PageToggleButton
                  key={pageNum}
                  number={pageNum}
                  isActive={isActive}
                  onClick={() => setSearchParams({ page: pageNum.toString() })}
                  color="awasi"
                  className="blog-pagination-button"
                />
              );
            })}
          </div>
        </div>
      )}


      {/* Featured Video Section - Full Viewport */}
      <div className="blog-featured-section" data-scroll-section="featured">
        <video 
          className="blog-featured-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="https://videos.ctfassets.net/hz1mviiqoqof/4lMIqOgZtZX77PLD9UfBk/984926e3d7c97a7595b2b434ca4394aa/parrot.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Call to Action Section */}
      <div className="blog-cta-section" data-scroll-section="cta">
        <div className="blog-cta-content">
          <h2 className="blog-cta-title">
            {t('pages/blog:cta.title')}
          </h2>
          <HandDrawnButton
            text={t('pages/blog:cta.button')}
            onClick={handleBooking}
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            size="normal"
          />
        </div>
      </div>


      <InstagramSection 
        title={t('pages/blog:instagram.title')}
        posts={[
          { src: images.instagram1, alt: "Instagram post 1" },
          { src: images.instagram2, alt: "Instagram post 2" },
          { src: images.instagram3, alt: "Instagram post 3" },
          { src: images.instagram4, alt: "Instagram post 4" }
        ]}
      />
      <Footer variant="default" />
    </div>
  );
};

export default BlogPage;